<template>
  <div :class="cardClasses">
    <!-- Card Header (optional) -->
    <div v-if="hasHeader" :class="headerClasses">
      <slot name="header">
        <h3 v-if="title" class="text-sm font-semibold text-gray-800 m-0">{{ title }}</h3>
      </slot>
    </div>

    <!-- Card Body -->
    <div :class="bodyClasses">
      <slot></slot>
    </div>

    <!-- Card Footer (optional) -->
    <div v-if="hasFooter" :class="footerClasses">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseCard',
  props: {
    title: {
      type: String,
      default: null
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'bordered', 'elevated', 'flat'].includes(value)
    },
    padding: {
      type: String,
      default: 'md',
      validator: (value) => ['none', 'sm', 'md', 'lg', 'xl'].includes(value)
    },
    rounded: {
      type: String,
      default: 'lg',
      validator: (value) => ['none', 'sm', 'md', 'lg', 'xl'].includes(value)
    },
    hoverable: {
      type: Boolean,
      default: false
    },
    overflow: {
      type: String,
      default: 'hidden',
      validator: (value) => ['hidden', 'visible', 'auto'].includes(value)
    }
  },
  computed: {
    hasHeader() {
      return this.$slots.header || this.title
    },
    hasFooter() {
      return this.$slots.footer
    },
    cardClasses() {
      return [
        'bg-white',
        this.variantClasses,
        this.roundedClasses,
        this.overflowClasses,
        this.hoverable ? 'transition-colors duration-200 hover:border-gray-300 cursor-pointer' : ''
      ]
    },
    overflowClasses() {
      const overflow = {
        hidden: 'overflow-hidden',
        visible: 'overflow-visible',
        auto: 'overflow-auto'
      }
      return overflow[this.overflow]
    },
    variantClasses() {
      const variants = {
        default: 'border border-gray-200',
        bordered: 'border border-gray-200',
        elevated: 'border border-gray-200',
        flat: 'border border-transparent'
      }
      return variants[this.variant]
    },
    roundedClasses() {
      const rounded = {
        none: 'rounded-none',
        sm: 'rounded',
        md: 'rounded-lg',
        lg: 'rounded-xl',
        xl: 'rounded-2xl'
      }
      return rounded[this.rounded]
    },
    headerClasses() {
      return [
        'border-b border-gray-100',
        this.paddingClasses
      ]
    },
    bodyClasses() {
      return [
        this.paddingClasses
      ]
    },
    footerClasses() {
      return [
        'border-t border-gray-100 bg-gray-50/50',
        this.paddingClasses
      ]
    },
    paddingClasses() {
      const paddings = {
        none: 'p-0',
        sm: 'p-3',
        md: 'p-4',
        lg: 'p-5',
        xl: 'p-6'
      }
      return paddings[this.padding]
    }
  }
}
</script>
